import { Button } from '@hue/design-system';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';

/// O QR do autenticador, em um lugar só.
///
/// Dois momentos pareiam: a ativação por convite e o primeiro acesso de quem recebeu o acesso já
/// criado, com senha definida por quem cadastrou (ADR 0032). O desenho é o mesmo, e duplicá-lo faria
/// um dos dois envelhecer sozinho.
export function MfaQr({ uri, className = '' }: { uri: string | null; className?: string }) {
  const [source, setSource] = useState<string>();

  useEffect(() => {
    if (!uri) return;
    let active = true;
    void import('qrcode')
      .then(({ default: QRCode }) =>
        QRCode.toString(uri, {
          type: 'svg',
          margin: 1,
          width: 200,
          color: { dark: '#0a0a0c', light: '#ffffff' },
        }),
      )
      .then((svg) => {
        if (active) setSource(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
      });
    return () => {
      active = false;
    };
  }, [uri]);

  return source ? (
    <div className={`rounded-2xl bg-white p-2 shadow-glow ${className}`}>
      <img src={source} alt="QR Code para configurar o MFA" className="aspect-square w-full" />
    </div>
  ) : (
    <div
      className={`aspect-square animate-pulse rounded-2xl bg-surface-elevated ${className}`}
      aria-label="Gerando QR Code"
    />
  );
}

/// O QR com as saídas para quem não tem como lê-lo: quem está no próprio celular não aponta a câmera
/// para a tela em que está (ADR 0081). A chave é a mesma do QR, mostrada só a quem já recebeu o QR.
export function MfaSetup({ uri, qrClassName = '' }: { uri: string; qrClassName?: string }) {
  const [copied, setCopied] = useState(false);
  const secret = new URL(uri).searchParams.get('secret') ?? '';
  const grouped = secret.match(/.{1,4}/gu)?.join(' ') ?? '';

  async function copy() {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="space-y-3">
      <MfaQr uri={uri} className={qrClassName} />
      <p className="text-center text-xs leading-5 text-content-muted">
        Leia o QR Code no seu aplicativo autenticador. Está no próprio celular? Abra no aplicativo
        ou copie a chave.
      </p>
      <p
        aria-label="Chave do autenticador"
        className="break-all text-center font-mono text-xs leading-5 text-content-secondary"
      >
        {grouped}
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={() => void copy()}>
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          {copied ? 'Chave copiada' : 'Copiar chave'}
        </Button>
        <a
          href={uri}
          className="inline-flex min-h-9 items-center gap-2 rounded-lg px-3 text-xs font-semibold text-brand-400 transition hover:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          <ExternalLink className="size-4" aria-hidden="true" />
          Abrir no aplicativo
        </a>
      </div>
    </div>
  );
}
